import { Trans } from '@lingui/react/macro';
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouteContext,
} from '@tanstack/react-router';

import { teamsControllerFindOne } from '@/api/core-api';
import { ImpersonationBanner } from '@/components/impersonation-banner/impersonation-banner';
import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { settingsV3QueryOptions } from '@/lib/feature-flags/settings-v3';
import { getClient } from '@/utils/get-api-client';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

import { BillingNav } from './billing/-billing-nav';

export const Route = createFileRoute('/researchers/_authenticated/billing')({
  beforeLoad: async ({ context, location }) => {
    const resolved = await resolveActiveDashboard({ context, location });
    const { organization, team } = resolved.activeDashboard;
    const isBillingV3Enabled =
      resolved.v3Enabled === true
        ? await context.queryClient
            .ensureQueryData(
              settingsV3QueryOptions(resolved.user.id, organization, team),
            )
            .catch(() => false)
        : false;
    let isLegacyPricing = false;
    let hasLegacyPricing = false;
    if (isBillingV3Enabled && organization && team) {
      const response = await teamsControllerFindOne({
        client: getClient(resolved.user.id),
        path: { organizationId: organization, teamId: team },
      });
      // A missing flag or a failed lookup is treated as legacy, like
      // PricingGuard does, so the team lands on classic billing instead of a
      // V3 page that 403s.
      isLegacyPricing = response.data?.newPricing !== true;
      hasLegacyPricing = response.data?.hasLegacyPricing === true;
    }
    if ((!isBillingV3Enabled || isLegacyPricing) && organization && team) {
      const env = await getPublicEnv();
      if (env.WEBSITE_URL) {
        const classicPage = location.pathname.endsWith('/spend-reports')
          ? 'lts/spend-reports'
          : isLegacyPricing
            ? 'credits'
            : location.pathname.endsWith('/history')
              ? 'history'
              : location.pathname.endsWith('/plans')
                ? 'plans'
                : location.pathname.endsWith('/credit-cards')
                  ? 'credit-cards'
                  : 'balances';
        throw redirect({
          href: `${env.WEBSITE_URL}/dashboard/organizations/${organization}/teams/${team}/billing/${classicPage}`,
        });
      }
      throw redirect({ to: '/researchers/projects' });
    }
    return { ...resolved, hasLegacyPricing };
  },
  component: BillingLayout,
});

function BillingLayout() {
  const { user, activeDashboard } = Route.useRouteContext();
  const { env, isAdminImpersonating, adminUser } = useRouteContext({
    from: '__root__',
  });

  return (
    <div className="flex h-screen flex-col print:block print:h-auto">
      {isAdminImpersonating && adminUser && (
        <ImpersonationBanner
          adminUser={adminUser}
          websiteUrl={env.WEBSITE_URL}
          impersonatedLabel={user.email}
        />
      )}
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        <div className="mx-auto flex w-full max-w-360 flex-col gap-6 px-6 py-8 lg:px-8">
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Plan & billing</Trans>
          </h1>
          <div className="flex flex-col gap-8 lg:flex-row">
            <BillingNav />
            <div className="min-w-0 flex-1 space-y-6">
              <Outlet />
            </div>
          </div>
        </div>
      </ResearcherShell>
    </div>
  );
}
